import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Image, Phone, Briefcase, Info, Palette, Newspaper, Menu, X, Globe, ChefHat } from "lucide-react";
import logo from "@/assets/logo-new.png";
import { useLanguage } from "@/contexts/LanguageContext";

const Navbar = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, setLanguage, t, isRTL } = useLanguage();
  
  const navItems = [
    { to: "/", icon: Home, labelKey: "nav.home" },
    { to: "/about-us", icon: Info, labelKey: "nav.about" },
    { to: "/services", icon: Palette, labelKey: "nav.services" },
    { to: "/aluminum", icon: Briefcase, labelKey: "nav.aluminum" },
    { to: "/kitchen-cabinets", icon: ChefHat, labelKey: "nav.kitchen" },
    { to: "/gallery", icon: Image, labelKey: "nav.gallery" },
    { to: "/articles", icon: Newspaper, labelKey: "nav.articles" },
    { to: "/contact", icon: Phone, labelKey: "nav.contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const toggleLanguage = () => {
    setLanguage(language === "ar" ? "en" : "ar");
  };

  return (
    <nav className="w-full bg-white shadow-lg sticky top-0 z-50 border-b border-border" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={logo} alt="ALFUNOON Group" className="h-14 w-auto" />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to}>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 ${
                  isActive(item.to) 
                    ? "bg-primary text-primary-foreground shadow-md" 
                    : "text-foreground hover:bg-primary/10 hover:text-primary"
                }`}>
                  <item.icon className="h-4 w-4" />
                  <span className="text-sm font-semibold whitespace-nowrap">{t(item.labelKey)}</span>
                </div>
              </Link>
            ))}
            
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 bg-muted text-foreground hover:bg-primary/10 hover:text-primary ${isRTL ? "mr-2" : "ml-2"}`}
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-semibold">{language === "ar" ? "EN" : "عربي"}</span>
            </button>
          </div>

          {/* Mobile Controls */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Language Toggle - Always visible */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-3 py-2 rounded-lg transition-all duration-300 bg-muted text-foreground hover:bg-primary/10 hover:text-primary"
            >
              <Globe className="h-5 w-5" />
              <span className="text-sm font-semibold">{language === "ar" ? "EN" : "عربي"}</span>
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-foreground hover:bg-primary/10 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link 
                  key={item.to} 
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    isActive(item.to) 
                      ? "bg-primary text-primary-foreground" 
                      : "text-foreground hover:bg-primary/10 hover:text-primary"
                  }`}>
                    <item.icon className="h-5 w-5" />
                    <span className="font-semibold">{t(item.labelKey)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
